/**
 * LoanChatFloatingButton — a floating chat launcher for the Loan Drilldown.
 *
 * Renders a fixed bottom-right FAB on every drilldown tab; clicking it opens a
 * popover card with the same LoanChatPanel used by the Chat tab. Lets the team
 * chat about a loan without leaving whatever tab they're on.
 *
 * Self-gating — returns null unless a loan is loaded AND canSeeLoanChat(email)
 * passes. Hidden while the Chat tab itself is open (the tab already shows the
 * panel, so the FAB would just duplicate the live connection).
 *
 * Mounted once in LoanDrilldownScreen; uses position:fixed so placement in the
 * tree doesn't matter.
 */
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import ForumRounded from "@mui/icons-material/ForumRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";

import { useAuth } from "../../../../auth/AuthProvider";
import LoanChatPanel from "./LoanChatPanel";
import { canSeeLoanChat } from "./loanChatAccess";
import { useLoanChatFabHidden } from "./useLoanChatFabHidden";
import { useLoanChatUnread } from "./useLoanChatUnread";

const BLUE = "#0a66ff";

// Default anchored placement of the FAB (px from the bottom-right corner). The
// popover is anchored separately; both get shifted by the same drag offset.
const BTN_RIGHT = 98;
const BTN_BOTTOM = 24;
const BTN_SIZE = 52;
// Popover anchoring + size, mirroring the <Paper> sx below. Used so the drag
// offset can keep the popover (not just the button) fully on screen.
const POP_RIGHT = 24;
const POP_BOTTOM = 92;
const POP_W = 440;
const POP_H = 680;
const POP_MAX_W_MARGIN = 48; // maxWidth: calc(100vw - 48px)
const POP_MAX_H_MARGIN = 130; // maxHeight: calc(100vh - 130px)
const EDGE_MARGIN = 8; // keep elements at least this far from any viewport edge
const DRAG_THRESHOLD = 4; // px of movement before a press becomes a drag (vs a click)

// Clamp the drag offset so that BOTH the button and the popover stay fully on
// screen. Each is anchored to the bottom-right (right/bottom) then translated by
// dx/dy; for an element of width w / height h anchored `right`/`bottom` px from
// the corner, keeping it within [EDGE_MARGIN, viewport - EDGE_MARGIN] gives a
// permitted dx/dy range. We intersect the button's range with the popover's.
const clampOffset = ({ dx, dy }) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Effective popover size honors its maxWidth/maxHeight caps.
  const popW = Math.min(POP_W, vw - POP_MAX_W_MARGIN);
  const popH = Math.min(POP_H, vh - POP_MAX_H_MARGIN);

  // Per-element [min, max] for dx and dy.
  const dxRange = (right, w) => [
    EDGE_MARGIN + w + right - vw, // left edge ≥ EDGE_MARGIN
    right - EDGE_MARGIN, // right edge ≤ vw - EDGE_MARGIN
  ];
  const dyRange = (bottom, h) => [
    EDGE_MARGIN + h + bottom - vh, // top edge ≥ EDGE_MARGIN
    bottom - EDGE_MARGIN, // bottom edge ≤ vh - EDGE_MARGIN
  ];

  const [btnDxMin, btnDxMax] = dxRange(BTN_RIGHT, BTN_SIZE);
  const [popDxMin, popDxMax] = dxRange(POP_RIGHT, popW);
  const [btnDyMin, btnDyMax] = dyRange(BTN_BOTTOM, BTN_SIZE);
  const [popDyMin, popDyMax] = dyRange(POP_BOTTOM, popH);

  // Intersection of both ranges (tightest min/max). Guard against an empty
  // intersection (tiny viewports) by falling back to the lower bound.
  const minDx = Math.max(btnDxMin, popDxMin);
  const maxDx = Math.max(minDx, Math.min(btnDxMax, popDxMax));
  const minDy = Math.max(btnDyMin, popDyMin);
  const maxDy = Math.max(minDy, Math.min(btnDyMax, popDyMax));

  return {
    dx: Math.min(maxDx, Math.max(minDx, dx)),
    dy: Math.min(maxDy, Math.max(minDy, dy)),
  };
};

export default function LoanChatFloatingButton() {
  const dealId = useSelector((s) => s.loanDrilldown?._id);
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useLoanChatFabHidden();
  // Position resets to the default anchor on every page load (intentionally not
  // persisted across refreshes).
  const [offset, setOffset] = useState({ dx: 0, dy: 0 });

  // Drag bookkeeping (kept in a ref so pointer handlers stay stable + closure-safe).
  const dragRef = useRef(null);

  // Re-clamp on window resize so a shrinking viewport can never strand the
  // button/popover off-screen.
  useEffect(() => {
    const onResize = () => setOffset((curr) => clampOffset(curr));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onChatTab = /\/chat\/?$/.test(location.pathname);

  // Gate: needs a loaded loan, access, not on the Chat tab, and not hidden by
  // the user (re-enable from the Chat tab).
  const gateOk =
    !!dealId && canSeeLoanChat(user?.email) && !onChatTab && !hidden;

  // Unread badge — messages from others since the user last opened this loan's
  // chat. Only polls while the bubble is visible AND closed; opening/closing
  // marks the loan's chat seen (advances the watermark, clears the badge).
  const { unread, markSeen } = useLoanChatUnread(dealId, {
    active: gateOk && !open,
  });

  if (!gateOk) return null;

  const toggleOpen = () => {
    setOpen((v) => !v);
    markSeen();
  };

  // ── Drag-to-reposition ─────────────────────────────────────────────────────
  // A press becomes a drag only after moving past DRAG_THRESHOLD; a press that
  // never crosses it is treated as a click (toggles the chat open/closed).
  const handlePointerDown = (e) => {
    if (e.button != null && e.button !== 0) return; // primary button / touch only
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseDx: offset.dx,
      baseDy: offset.dy,
      moved: false,
      pointerId: e.pointerId,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const deltaX = e.clientX - d.startX;
    const deltaY = e.clientY - d.startY;
    if (!d.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;
    d.moved = true;
    setOffset(clampOffset({ dx: d.baseDx + deltaX, dy: d.baseDy + deltaY }));
  };

  const handlePointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    e.currentTarget.releasePointerCapture?.(d.pointerId);
    // A drag just leaves the button where it landed (offset already updated on
    // move); only a press that never moved counts as a click.
    if (!d.moved) toggleOpen();
  };

  const dragTransform = `translate(${offset.dx}px, ${offset.dy}px)`;

  return (
    <>
      {/* Popover card */}
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            right: 24,
            bottom: 92,
            transform: dragTransform,
            zIndex: 1250,
            width: 440,
            maxWidth: "calc(100vw - 48px)",
            height: 680,
            maxHeight: "calc(100vh - 130px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "14px",
            border: "1px solid #e3e9ec",
          }}
        >
          <LoanChatPanel
            dealId={dealId}
            onClose={() => setOpen(false)}
            headerActions={
              <Tooltip title="Hide the chat bubble (re-enable on the Chat tab)">
                <IconButton
                  size="small"
                  aria-label="Hide chat bubble"
                  onClick={() => {
                    setHidden(true);
                    setOpen(false);
                  }}
                >
                  <VisibilityOffOutlined
                    sx={{ fontSize: 18, color: "#64748b" }}
                  />
                </IconButton>
              </Tooltip>
            }
          />
        </Paper>
      )}

      {/* Launcher — also the drag handle (drag to reposition, click to toggle). */}
      <Tooltip
        title={open ? "Close chat" : "Loan chat · drag to move"}
        placement="left"
      >
        <Box
          component="button"
          type="button"
          aria-label="Toggle loan chat"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          sx={{
            position: "fixed",
            right: 98,
            bottom: 24,
            transform: dragTransform,
            zIndex: 1250,
            width: 52,
            height: 52,
            borderRadius: "50%",
            border: "none",
            cursor: "grab",
            touchAction: "none",
            "&:active": { cursor: "grabbing" },
            background: open ? "#0a5ae0" : BLUE,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 6px 20px rgba(10,102,255,0.4)",
            transition: "background 0.12s ease",
            "&:hover": { background: "#0a5ae0" },
          }}
        >
          {open ? (
            <CloseRounded sx={{ fontSize: 24 }} />
          ) : (
            <ForumRounded sx={{ fontSize: 24 }} />
          )}
          {/* Unread badge — only when closed and there are new messages. */}
          {!open && unread > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: -3,
                right: -3,
                minWidth: 20,
                height: 20,
                px: "5px",
                borderRadius: "999px",
                background: "#ef4444",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #fff",
                boxSizing: "border-box",
              }}
            >
              {unread > 9 ? "9+" : unread}
            </Box>
          )}
        </Box>
      </Tooltip>
    </>
  );
}
